// Página de Login - Autenticación de usuarios
// Permite a los usuarios iniciar sesión o registrarse

import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { useBusinessSettingsContext } from '../hooks/useBusinessSettingsContext'
import Button from '../components/Button'
import Logo from '../components/Logo'
import './Login.css'

/**
 * Página de Login
 * Formulario para iniciar sesión o registrarse
 */
function Login() {
  // Hook de navegación de React Router
  const navigate = useNavigate()
  
  // Hook de autenticación
  const { login, register, currentUser } = useAuth()
  const { activeProfile } = useBusinessSettingsContext()
  
  // Estado del formulario
  const [isLogin, setIsLogin] = useState(true) // true = login, false = register
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  // Si el usuario ya está autenticado, redirigir a home
  if (currentUser) {
    navigate('/')
    return null
  }

  /**
   * Manejar el envío del formulario
   * @param {Event} e - Evento del formulario
   */
  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      let result

      if (isLogin) {
        // Intentar iniciar sesión
        result = await login(email, password)
      } else {
        // Validar que el nombre esté presente en registro
        if (!displayName.trim()) {
          setError('El nombre es requerido')
          setLoading(false)
          return
        }
        // Intentar registrar
        result = await register(email, password, displayName)
      }

      if (result.success) {
        // Redirigir a home después de login/registro exitoso
        navigate('/')
      } else {
        // Mostrar error
        setError(result.error || 'Ocurrió un error')
      }
    } catch (err) {
      setError('Ocurrió un error inesperado')
      console.error('Error en login/register:', err)
    } finally {
      setLoading(false)
    }
  }

  /**
   * Cambiar entre modo login y registro
   */
  const toggleMode = () => {
    setIsLogin(!isLogin)
    setError('')
    setEmail('')
    setPassword('')
    setDisplayName('')
  }

  const isFashion = activeProfile?.rubro === 'indumentaria' && (activeProfile?.subrubro || 'femenina') === 'femenina'

  if (isFashion) {
    return (
      <main className="min-h-[calc(100vh-160px)] w-full px-6 py-12 flex items-center justify-center bg-[#f8f7f6]">
        <div className="w-full max-w-xl bg-white rounded-[32px] border border-[#eee] overflow-hidden shadow-[0_24px_60px_rgba(0,0,0,0.08)] p-10 sm:p-12">
          <div className="flex flex-col items-center text-center gap-4">
            <Logo className="h-10 w-auto" />
            <div className="space-y-2">
              <h2 className="text-3xl font-bold text-[#181211]">
                {isLogin ? 'Iniciar Sesión' : 'Crear Cuenta'}
              </h2>
              <p className="text-sm text-gray-500">
                {isLogin ? 'Ingresa con tu cuenta para continuar.' : 'Regístrate para comenzar a comprar.'}
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="mt-8 space-y-5">
            {!isLogin && (
              <div className="space-y-2">
                <label htmlFor="displayName" className="text-xs font-bold uppercase tracking-wider text-gray-500">
                  Nombre Completo
                </label>
                <input
                  type="text"
                  id="displayName"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Tu nombre"
                  required={!isLogin}
                  disabled={loading}
                  className="w-full px-4 py-3 bg-white border border-[#e8e6e1] rounded-lg text-sm outline-none focus:ring-1 focus:ring-[#c27e70] focus:border-[#c27e70]"
                />
              </div>
            )}

            <div className="space-y-2">
              <label htmlFor="email" className="text-xs font-bold uppercase tracking-wider text-gray-500">
                Email
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@email.com"
                required
                disabled={loading}
                className="w-full px-4 py-3 bg-white border border-[#e8e6e1] rounded-lg text-sm outline-none focus:ring-1 focus:ring-[#c27e70] focus:border-[#c27e70]"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="password" className="text-xs font-bold uppercase tracking-wider text-gray-500">
                Contraseña
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                disabled={loading}
                minLength={6}
                className="w-full px-4 py-3 bg-white border border-[#e8e6e1] rounded-lg text-sm outline-none focus:ring-1 focus:ring-[#c27e70] focus:border-[#c27e70]"
              />
              {!isLogin && (
                <p className="text-xs text-gray-400">Mínimo 6 caracteres</p>
              )}
            </div>

            {error && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#c27e70] hover:bg-[#a66a5e] text-white py-3 rounded-lg font-semibold text-sm tracking-wide transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading
                ? (isLogin ? 'Iniciando sesión...' : 'Creando cuenta...')
                : (isLogin ? 'Iniciar Sesión' : 'Crear Cuenta')}
            </button>
          </form>

          <div className="mt-6 text-center text-sm text-gray-500">
            {isLogin ? '¿No tienes una cuenta? ' : '¿Ya tienes una cuenta? '}
            <button
              type="button"
              onClick={toggleMode}
              className="font-semibold text-[#c27e70] underline"
              disabled={loading}
            >
              {isLogin ? 'Regístrate' : 'Inicia Sesión'}
            </button>
          </div>

          <div className="mt-6 text-center">
            <Link to="/" className="text-xs uppercase tracking-[0.2em] text-gray-400 hover:text-[#c27e70]">
              Volver al inicio
            </Link>
          </div>
        </div>
      </main>
    )
  }

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-card">
          {/* Logo */}
          <div className="login-header">
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
              <Logo height={60} />
            </div>
            <h2>{isLogin ? 'Iniciar Sesión' : 'Crear Cuenta'}</h2>
            <p>
              {isLogin 
                ? 'Ingresa tus credenciales para continuar' 
                : 'Crea una cuenta para comenzar'}
            </p>
          </div>

          {/* Formulario */}
          <form onSubmit={handleSubmit} className="login-form">
            {/* Campo de nombre (solo en registro) */}
            {!isLogin && (
              <div className="form-group">
                <label htmlFor="displayName">Nombre Completo</label>
                <input
                  type="text"
                  id="displayName"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Tu nombre"
                  required={!isLogin}
                  disabled={loading}
                />
              </div>
            )}

            {/* Campo de email */}
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@email.com"
                required
                disabled={loading}
              />
            </div>

            {/* Campo de contraseña */}
            <div className="form-group">
              <label htmlFor="password">Contraseña</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                disabled={loading}
                minLength={6}
              />
              {!isLogin && (
                <small className="form-hint">
                  Mínimo 6 caracteres
                </small>
              )}
            </div>

            {/* Mensaje de error */}
            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            {/* Botón de submit */}
            <Button
              type="submit"
              variant="primary"
              size="large"
              disabled={loading}
              className="login-button"
            >
              {loading 
                ? (isLogin ? 'Iniciando sesión...' : 'Creando cuenta...') 
                : (isLogin ? 'Iniciar Sesión' : 'Crear Cuenta')}
            </Button>
          </form>

          {/* Enlace para cambiar entre login y registro */}
          <div className="login-footer">
            <p>
              {isLogin ? '¿No tienes una cuenta? ' : '¿Ya tienes una cuenta? '}
              <button 
                type="button" 
                onClick={toggleMode}
                className="link-button"
                disabled={loading}
              >
                {isLogin ? 'Regístrate' : 'Inicia Sesión'}
              </button>
            </p>
          </div>

          {/* Enlace para volver a home */}
          <div className="login-back">
            <Link to="/" className="back-link">
              ← Volver al inicio
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Login

